import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import CardGrid from '@features/trend-research/components/common/CardGrid';
import {
	Card,
	CardContent,
	FabricImageContainer,
	TopBar
} from '@features/trend-research/components/common/TrendCard';
import { DataSource, Divider, Product } from '@features/trend-research/components/details/components';
import {
	CategoryItems,
	FilterButtons,
	SubCategoryItems
} from '@features/trend-research/components/details/components/Filters';
import Charts from '@features/trend-research/components/details/Fabric/Charts';
import ProductTopSection from '@features/trend-research/components/details/Fabric/ProductTopSection';
import useApi from '@features/trend-research/hooks/useApi';
import useQueryString from '@features/trend-research/hooks/useQueryString';
import {
	_getBasedOnBrandData,
	areArraysEqual,
	cleanUrl,
	detailsCountHeaders,
	formatFabricName,
	getDetailsCount,
	sourceType
} from '@features/trend-research/utils/utils';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import {
	findAllBrandInsight,
	findBrandInsight,
	findTrendResearchProductsBySource,
	findTrendResearchUserPreference
} from '@services/trendResearch';

const Fabric = () => {
	const dispatch = useDispatch();
	const [selectedItem, setSelectedItem] = useState(null);
	const [selectedSubCategory, setSelectedSubCategory] = useState([]);
	const [page, setPage] = useState(0);
	const [isCloseKeyFabric, setIsCloseKeyFabric] = useState(false);
	const [newRelatedProducts, setNewRelatedProducts] = useState([]);
	const [queryStringForApi, setQueryStringForApi] = useState('');
	const [detailsCount, setDetailsCount] = useState({});
	const [fabricList, setFabricList] = useState([]);
	const [totalPage, setTotalPage] = useState(0);
	const [brands, setBrands] = useState([]);
	// const [influencers, setInfluencers] = useState([]);

	const queryString = useQueryString(); // Call the custom hook
	const location = useLocation();

	const { data } = useApi(findTrendResearchUserPreference, '', queryString);
	const { data: fabricData } = useApi(findBrandInsight, 'fibers', queryString);
	const { source } = location?.state || {};

	useEffect(() => {
		setQueryStringForApi(queryString);
	}, [queryString]);

	useEffect(() => {
		if (location?.state && location?.state?.selectedItem) {
			setSelectedItem(location?.state?.selectedItem);
			setSelectedSubCategory(formatFabricName(location?.state?.selectedItem?.fabricName));
		}
	}, [location.state]);
	const fetchDetailsCount = async () => {
		try {
			dispatch(openLoader());
			const response = await findBrandInsight('details-count', queryStringForApi);
			setDetailsCount((prev) => ({
				...prev,
				maxPrice: response?.maxPrice,
				midPrice: response?.midPrice,
				minPrice: response?.minPrice
			}));
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const getUserPreference = async () => {
		try {
			const data = await findTrendResearchUserPreference();
			setFabricList(data?.fabricTypeList);
		} catch {
			/* Empty Block */
		}
	};
	const getAllBrands = async () => {
		try {
			dispatch(openLoader());
			const data = await findAllBrandInsight();
			setBrands(data);

			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};
	// const getInfluencers = async () => {
	// 	try {
	// 		dispatch(openLoader());
	// 		const data = await findAllInstagramInfluencer();
	// 		setInfluencers(data);
	// 		// dispatch(setInstagramInfluencerList(data));
	// 		// if (selectedInfluencers.length === 0) {
	// 		// 	dispatch(toggleAllInstagramInfluencerList(data.map((item) => item.userName)));
	// 		// }
	// 		dispatch(closeLoader());
	// 	} catch {
	// 		dispatch(closeLoader());
	// 	}
	// };
	useEffect(() => {
		if (source === 'Brand') {
			getAllBrands();
		}
		// if (source === 'Instagram') {
		// 	getInfluencers();
		// }
	}, [source]);

	useEffect(() => {
		getUserPreference();
	}, []);

	useEffect(() => {
		if (source !== 'Instagram' && queryStringForApi !== '') {
			fetchDetailsCount(queryStringForApi);
		}
	}, [queryStringForApi]);

	const fetchProducts = async (queryString, offset = 0) => {
		try {
			dispatch(openLoader());
			if (page <= totalPage) {
				const response = await findTrendResearchProductsBySource(
					sourceType[source],
					queryString + `&page=${offset}&size=20`
				);
				if (!response.data.length) {
					dispatch(closeLoader());
					return;
				}
				setTotalPage(response.totalPages);
				setDetailsCount((prev) => ({
					...prev,
					noOfProducts: response?.totalElements
				}));
				setNewRelatedProducts((prevProducts) => [...prevProducts, ...response.data]);
			}
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};
	useEffect(() => {
		if (queryStringForApi) {
			fetchProducts(queryStringForApi, page);
		}
	}, [page, queryStringForApi]);

	const handleSelectionChange = (newSelectedItems) => {
		setSelectedSubCategory(newSelectedItems);
		const newFiberList = newSelectedItems.join(',');

		const searchParams = new URLSearchParams(queryStringForApi);
		if (searchParams.has('fiberList')) {
			searchParams.delete('fiberList');
		}
		if (newFiberList !== 'All') {
			searchParams.append('fiberList', newFiberList);
		}
		const updatedQueryString = `?${searchParams.toString()}`;
		setNewRelatedProducts([]);
		setQueryStringForApi(updatedQueryString);
		setPage(0);
		window.scrollTo(0, 600);
	};

	const handleScroll = async () => {
		if (
			window.innerHeight + document.documentElement.scrollTop + 1 >=
			document.documentElement.scrollHeight
		) {
			setPage((prev) => prev + 1);
		}
	};

	useEffect(() => {
		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	const onClose = () => {
		setIsCloseKeyFabric(true);
		const searchParams = new URLSearchParams(queryStringForApi);
		if (searchParams.has('fiberList')) {
			searchParams.delete('fiberList');
		}

		const updatedQueryString = `?${searchParams.toString()}`;
		setNewRelatedProducts([]);
		setQueryStringForApi(updatedQueryString);
		setPage(0);
		setSelectedSubCategory([]);
	};
	const [mainImage, ...images] = selectedItem?.imageUrlList?.map(cleanUrl) || [];

	return (
		<>
			{brands?.length > 0 && (
				<CardGrid
					cards={_getBasedOnBrandData(
						queryString,
						data?.productGroupResponseList,
						fabricList,
						brands
					)}
				/>
			)}
			<Charts />
			<FilterButtons>
				<CategoryItems items={{ Composition: fabricData?.fiberCount }} />
				<SubCategoryItems
					items={fabricData?.fiberWiseProductCount}
					selectedItems={selectedSubCategory}
					onSelectionChange={handleSelectionChange}
					multiple
					isSlideShow
					showAll
					scrollTop={300}
				/>
			</FilterButtons>

			{isCloseKeyFabric ||
				(areArraysEqual(
					selectedSubCategory,
					formatFabricName(location?.state?.selectedItem?.fabricName)
				) && (
					<div className="key-fabric-section pt-3 trend-padding">
						<div className="key-slider grid-column-4">
							<Card className="single-fabric-item edit-key-theme-item">
								<TopBar topText={location?.state?.selectedItem?.isTop} onClose={onClose} />
								<FabricImageContainer mainImage={mainImage} images={images} />
								<CardContent
									additionalClasses="pt-3"
									details={{
										title: selectedItem?.fabricName,
										itemCount: selectedItem?.fabricCount,
										percentage: selectedItem?.percentage
									}}
								/>
							</Card>
						</div>
					</div>
				))}
			<Divider />
			<div className="trending-product border-0 top-fabric-product">
				<DataSource data={getDetailsCount(detailsCount)} headers={detailsCountHeaders} />
				<ProductTopSection title="Related Products" />
				<Product allProducts={newRelatedProducts} />
			</div>
		</>
	);
};

export default Fabric;
